/**
 * StudentCertificatesTab.jsx
 * Kullanicinin sertifikalarini listeler.
 * Header button: Sertifika Oluştur (admin manuel issue, modal + RichSelect).
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import { getApiErrorMessage } from '../../utils/apiError';
import { EmptyState, RichSelect } from '../../components/admin';
import { useIssueCertificate } from '../../api/queries/students';
import { Award, ExternalLink, Plus, X } from 'lucide-react';

export default function StudentCertificatesTab({ detail, userId, onRefresh }) {
  const { t, lang } = useLanguage();
  const toast = useToast();

  const certs = detail?.certificates || [];
  const enrollments = detail?.enrollments || [];
  const certifiedCourseIds = new Set(certs.map((c) => c.course_id));

  const [issueOpen, setIssueOpen] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState('');

  const issueCertificate = useIssueCertificate(userId);

  const courseOptions = enrollments.map((e) => ({
    value: e.course_id,
    label: e.course_title || e.title || e.course_id,
    sublabel: certifiedCourseIds.has(e.course_id)
      ? t('admin.studentHub.issue_certificate_already_suffix')
      : (lang === 'tr'
        ? { active: 'Aktif', completed: 'Tamamlandı', paused: 'Duraklatıldı', cancelled: 'İptal' }[e.status] || e.status
        : e.status),
  }));

  const closeIssueModal = () => {
    setIssueOpen(false);
    setSelectedCourseId('');
  };

  const handleIssue = async () => {
    if (!selectedCourseId) return;
    const alreadyExists = certifiedCourseIds.has(selectedCourseId);
    try {
      await issueCertificate.mutateAsync(selectedCourseId);
      toast.success(
        alreadyExists
          ? t('admin.studentHub.issue_certificate_already_exists')
          : t('admin.studentHub.issue_certificate_success')
      );
      closeIssueModal();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const issueButton = (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => setIssueOpen(true)}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 border-[var(--brand-primary)] bg-[var(--brand-primary)] text-white text-xs font-bold"
      data-testid="issue-certificate-btn"
    >
      <Plus size={14} />
      {t('admin.studentHub.btn_issue_certificate')}
    </motion.button>
  );

  const issueModal = (
    <AnimatePresence>
      {issueOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !issueCertificate.isPending && closeIssueModal()}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="bg-white rounded-2xl border-2 border-[var(--border-light)] p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="issue-certificate-modal"
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display text-lg font-bold text-[var(--text-main)]">
                {t('admin.studentHub.issue_certificate_title')}
              </h3>
              <button
                onClick={closeIssueModal}
                disabled={issueCertificate.isPending}
                className="p-1 rounded-lg hover:bg-gray-100 disabled:opacity-40"
              >
                <X size={16} />
              </button>
            </div>

            <p className="text-xs text-[var(--text-muted)] mb-4">
              {t('admin.studentHub.issue_certificate_desc')}
            </p>

            {enrollments.length === 0 ? (
              <p className="text-sm text-[var(--text-muted)] mb-2">
                {t('admin.studentHub.issue_certificate_empty')}
              </p>
            ) : (
              <>
                <label className="block text-xs font-bold text-[var(--text-muted)] mb-1.5">
                  {t('admin.studentHub.issue_certificate_label')}
                </label>
                <RichSelect
                  options={courseOptions}
                  value={selectedCourseId}
                  onChange={setSelectedCourseId}
                  placeholder={t('admin.studentHub.issue_certificate_placeholder')}
                  disabled={issueCertificate.isPending}
                  className="mb-4"
                />
              </>
            )}

            <div className="flex gap-2 mt-2">
              <button
                onClick={closeIssueModal}
                disabled={issueCertificate.isPending}
                className="flex-1 px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] text-sm font-bold disabled:opacity-40"
              >
                {t('admin.btn_cancel')}
              </button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleIssue}
                disabled={!selectedCourseId || issueCertificate.isPending}
                className="flex-1 px-4 py-2.5 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-bold border-b-4 border-[var(--brand-primary-shadow)] disabled:opacity-40"
                data-testid="issue-certificate-confirm-btn"
              >
                {issueCertificate.isPending ? '...' : t('admin.studentHub.issue_certificate_confirm')}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (certs.length === 0) {
    return (
      <div>
        <div className="flex items-center justify-end mb-3">{issueButton}</div>
        <EmptyState title={lang === 'tr' ? 'Sertifika yok' : 'No certificates'} icon={<Award size={40} strokeWidth={1.5} />} />
        {issueModal}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-end mb-3">{issueButton}</div>
      <div className="space-y-2">
      {certs.map((cert, i) => (
        <div
          key={cert.certificate_id || cert._id || i}
          className="flex items-center gap-3 p-3 rounded-xl border-2 border-[var(--border-light)] hover:bg-gray-50"
          data-testid={`cert-row-${i}`}
        >
          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Award size={16} className="text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm truncate">
              {cert.course_title || cert.certificate_type || '-'}
            </p>
            {cert.issued_at && (
              <p className="text-[10px] text-[var(--text-muted)]">
                {new Date(cert.issued_at).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US')}
              </p>
            )}
          </div>
          {cert.verification_url && (
            <a
              href={cert.verification_url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded-lg border border-[var(--border-light)] hover:bg-gray-100 text-[var(--text-muted)] hover:text-[var(--text-main)]"
              title={t('admin.studentHub.cert_col_verify')}
            >
              <ExternalLink size={14} />
            </a>
          )}
        </div>
      ))}
      </div>
      {issueModal}
    </div>
  );
}
